import type { JSX } from "react";
import React from "react";
import type {
  AppClassProperties,
  AppProps,
  AppState,
  Device,
  ExcalidrawProps,
  UIAppState,
} from "../types";
import type { ActionManager } from "../actions/manager";
import { t } from "../i18n";
import { showSelectedShapeActions } from "../element";
import type { NonDeletedExcalidrawElement } from "../element/types";
import { FixedSideContainer } from "./FixedSideContainer";
import { Island } from "./Island";
import { HintViewer } from "./HintViewer";
import { calculateScrollCenter } from "../scene";
import { SelectedShapeActions, renderShapeToolButton } from "./Actions";
import { Section } from "./Section";
import { SCROLLBAR_WIDTH, SCROLLBAR_MARGIN } from "../scene/scrollbars";
import { useTunnels } from "../context/tunnels";
import { SHAPES } from "../shapes";

type MobileMenuProps = {
  appState: UIAppState;
  actionManager: ActionManager;
  renderJSONExportDialog: () => React.ReactNode;
  renderImageExportDialog: () => React.ReactNode;
  setAppState: React.Component<any, AppState>["setState"];
  elements: readonly NonDeletedExcalidrawElement[];
  onLockToggle: () => void;
  onHandToolToggle: () => void;
  onPenModeToggle: AppClassProperties["togglePenMode"];

  renderTopRightUI?: (
    isMobile: boolean,
    appState: UIAppState,
  ) => JSX.Element | null;
  renderCustomStats?: ExcalidrawProps["renderCustomStats"];
  renderSidebars: () => JSX.Element | null;
  device: Device;
  renderWelcomeScreen: boolean;
  UIOptions: AppProps["UIOptions"];
  app: AppClassProperties;
};

// pro100доска: на телефоне те же 4 инструмента, что и в вертикальном доке
// на десктопе (см. VerticalToolbar.tsx) - остальные фигуры убраны намеренно
const MOBILE_TOOLS = ["selection", "freedraw", "eraser", "text"] as const;

export const MobileMenu = ({
  appState,
  elements,
  actionManager,
  setAppState,
  renderTopRightUI,
  renderSidebars,
  device,
  renderWelcomeScreen,
  app,
}: MobileMenuProps) => {
  const { WelcomeScreenCenterTunnel, MainMenuTunnel } = useTunnels();

  const isEditable =
    !appState.viewModeEnabled &&
    appState.openDialog?.name !== "elementLinkSelector";

  return (
    <>
      {renderSidebars()}
      {/* верхняя панель ("Мои доски", статус, клетки, поделиться) рисуется
          порталом из excalidraw-app renderTopRightUI(isMobile=true) */}
      {renderTopRightUI && renderTopRightUI(true, appState)}
      <FixedSideContainer side="top" className="App-top-bar mobile-top-hints">
        {renderWelcomeScreen && <WelcomeScreenCenterTunnel.Out />}
        <HintViewer
          appState={appState}
          isMobile={true}
          device={device}
          app={app}
        />
      </FixedSideContainer>
      <div
        className="App-bottom-bar"
        style={{
          marginBottom: SCROLLBAR_WIDTH + SCROLLBAR_MARGIN * 2,
          marginLeft: SCROLLBAR_WIDTH + SCROLLBAR_MARGIN * 2,
          marginRight: SCROLLBAR_WIDTH + SCROLLBAR_MARGIN * 2,
        }}
      >
        <Island padding={0}>
          {appState.openMenu === "shape" &&
          isEditable &&
          showSelectedShapeActions(appState, elements) ? (
            <Section className="App-mobile-menu" heading="selectedShapeActions">
              <SelectedShapeActions
                appState={appState}
                elementsMap={app.scene.getNonDeletedElementsMap()}
                renderAction={actionManager.renderAction}
                app={app}
              />
            </Section>
          ) : null}
          <footer className="App-toolbar mobile-dock">
            {isEditable && (
              <div className="mobile-dock__tools">
                {MOBILE_TOOLS.map((value) =>
                  renderShapeToolButton(
                    SHAPES.find((s) => s.value === value)!,
                    appState,
                    app,
                  ),
                )}
              </div>
            )}
            <div className="mobile-dock__actions">
              {isEditable && actionManager.renderAction("toggleEditMenu")}
              {isEditable && actionManager.renderAction("undo")}
              {isEditable && actionManager.renderAction("redo")}
              <MainMenuTunnel.Out />
            </div>
            {appState.scrolledOutside &&
              !appState.openMenu &&
              !appState.openSidebar && (
                <button
                  type="button"
                  className="scroll-back-to-content"
                  onClick={() => {
                    setAppState((appState) => ({
                      ...calculateScrollCenter(elements, appState),
                    }));
                  }}
                >
                  {t("buttons.scrollBackToContent")}
                </button>
              )}
          </footer>
        </Island>
      </div>
    </>
  );
};
